/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useTranslations } from '@/hooks/useTranslations';
import {
    freApi,
    useCreateUsersMutation,
    useGetProductRunByUserIdAndFlowIdQuery,
    useGetUserInfoQuery,
    useGetUsersQuery,
    useLaunch42kSkuMutation,
    useLaunchAribaSkuMutation,
    useLaunchAribaNexusSkuMutation,
} from '@/store/apis';
import { actions, fetchStepsByPhaseIdAndRunId, updateConfigValue, purgeExistingData } from '@/store/Products';
import { PhaseState, IStep } from '@/types';
import { Bar, Button, FlexBox, MessageBox, MessageBoxType, Text } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useOpen from '@/hooks/useOpen';
import axiosInstance from '@/utils/axiosInstance';
import { formatDate } from '../JobTable/JobTableUtils';

const BottomActions = () => {
    const { t } = useTranslations();
    const { flowId } = useParams();
    const productList = useAppSelector(state => state?.productList);
    const {
        currentSKU,
        currentPhase,
        currentStep,
        aPhases,
        currentRunId,
        isFinalPage,
        unansweredQuestions,
        currentFlowId,
        showHelpVideo,
        commodityCode,
        currentProductConfigurationValue,
    } = productList;
    const dispatch = useAppDispatch();
    const [createUser] = useCreateUsersMutation();
    const { data: creatingUsers = [] } = useGetUsersQuery(currentRunId, {
        skip: !currentRunId,
    });
    const [launchAriba, { error: launchAribaError, reset: launchAribaReset, isSuccess: isAribaSuccess }] = useLaunchAribaSkuMutation();
    const [launch42k, { error, reset, isSuccess }] = useLaunch42kSkuMutation();
    const [launchAribaNexus, { isSuccess: isAribaNexusSuccess }] = useLaunchAribaNexusSkuMutation();

    const { data: userInfo } = useGetUserInfoQuery();
    const { data: productRun } = useGetProductRunByUserIdAndFlowIdQuery(
        {
            userId: userInfo?.scim_id ?? '',
            flowId: flowId ?? '',
        },
        { skip: currentSKU.SKUId === null || !userInfo || !flowId }
    );
    const [currentCommodityCode, setCurrentCommodityCode] = useState<string>();
    const [isOpen, toggle] = useOpen();
    const [buttonClicked, setButtonClicked] = useState<string>();

    useEffect(() => {
        if (currentPhase.id && productRun) {
            dispatch(fetchStepsByPhaseIdAndRunId({ phaseId: currentPhase.id, runId: productRun?.id }));
        }
    }, [currentPhase]);

    useEffect(() => {
        if (isAribaSuccess || isSuccess || isAribaNexusSuccess) {
            dispatch(actions.setIsFinalPage(true));
        }
    }, [isAribaSuccess, isSuccess, isAribaNexusSuccess]);

    useEffect(() => {
        if (commodityCode.commodityCodeType) {
            setCurrentCommodityCode(commodityCode.commodityCodeType);
        }
    }, [commodityCode]);

    interface CheckForCommodityCodeParams {
        isProductClassificationSystemTask: boolean;
    }

    const checkForCommodityCode = async ({ isProductClassificationSystemTask }: CheckForCommodityCodeParams): Promise<boolean> => {
        const isCommodityCodeExist = await axiosInstance.get(`/commodity-codes-run/exist/${currentRunId}`);
        return (
            isProductClassificationSystemTask &&
            currentCommodityCode !== currentProductConfigurationValue?.personalizationConfiguration?.configurationValue &&
            isCommodityCodeExist.data
        );
    };

    const gotoNextStep = async () => {
        const isProductClassificationSystemTask =
            (currentStep as IStep).name === 'Product Classification System' || (currentStep as IStep).title === 'Product Classification System'; // TODO: Change to get condition from backend instead of hardcoded
        if (isProductClassificationSystemTask) {
            const isCheckForCommodityCodeRequired = await checkForCommodityCode({ isProductClassificationSystemTask });
            if (isCheckForCommodityCodeRequired) {
                toggle();
                setButtonClicked('CONTINUE_BUTTON');
                return;
            }
        }
        const isCreateUserTask = (currentPhase as PhaseState).phaseName === 'Users' || (currentPhase as PhaseState).name === 'Users'; // TODO: Change to get condition from backend instead of hardcoded

        if (isCreateUserTask) {
            try {
                await createUser(creatingUsers);
                dispatch(actions.setNextStep());
                return;
            } catch (e) {
                console.error('Create user failed:::', e);
            }
        }

        if (showHelpVideo) {
            dispatch(actions.setShowHelpVideo(false));
            return;
        }

        await dispatch(updateConfigValue(false));
        dispatch(freApi.util.invalidateTags(['Subscription']));
        dispatch(actions.setNextStep());
    };

    const gotoPreviousStep = async () => {
        const isProductClassificationSystemTask =
            (currentStep as IStep).name === 'Product Classification System' || (currentStep as IStep).title === 'Product Classification System'; // TODO: Change to get condition from backend instead of hardcoded
        if (isProductClassificationSystemTask) {
            const isCheckForCommodityCodeRequired = await checkForCommodityCode({ isProductClassificationSystemTask });
            if (isCheckForCommodityCodeRequired) {
                toggle();
                setButtonClicked('PREVIOUS_BUTTON');
                return;
            }
        }
        const isCreateUserTask = (currentPhase as PhaseState).phaseName === 'Users' || (currentPhase as PhaseState).name === 'Users'; // TODO: Change to get condition from backend instead of hardcoded

        if (isCreateUserTask) {
            try {
                await createUser(creatingUsers);
                dispatch(actions.setPreviousStep());
                return;
            } catch (e) {
                console.error('Create user failed:::', e);
            }
        }
        await dispatch(updateConfigValue(false));
        dispatch(freApi.util.invalidateTags(['Subscription']));
        dispatch(actions.setPreviousStep());
    };

    const launch = async () => {
        const payload = {
            runId: currentRunId,
        };

        if (currentSKU?.SKUId === 1) {
            await launch42k(payload);
        } else if (currentSKU?.SKUId == 2) {
            launchAribaNexus(payload);
        } else {
            await launchAriba(payload);
        }
    };

    const gotoReview = async () => {
        const isProductClassificationSystemTask =
            (currentStep as IStep).name === 'Product Classification System' || (currentStep as IStep).title === 'Product Classification System'; // TODO: Change to get condition from backend instead of hardcoded
        if (isProductClassificationSystemTask) {
            const isCheckForCommodityCodeRequired = await checkForCommodityCode({ isProductClassificationSystemTask });
            if (isCheckForCommodityCodeRequired) {
                toggle();
                setButtonClicked('REVIEW_BUTTON');
                return;
            }
        }
        const isCreateUserTask = (currentPhase as PhaseState).phaseName === 'Users' || (currentPhase as PhaseState).name === 'Users'; // TODO: Change to get condition from backend instead of hardcoded

        const reviewPhase = aPhases[aPhases.length - 1];
        if (isCreateUserTask) {
            try {
                await createUser(creatingUsers);
                dispatch(actions.setCurrentPhase(reviewPhase));
                return;
            } catch (e) {
                console.error('Create user failed:::', e);
            }
        }
        await dispatch(updateConfigValue(false));
        dispatch(freApi.util.invalidateTags(['Subscription']));
        dispatch(actions.setCurrentPhase(reviewPhase));
    };

    const onDomainChangeOkButtoClicked = async () => {
        await dispatch(purgeExistingData());
        await dispatch(updateConfigValue(false));
        dispatch(freApi.util.invalidateTags(['Subscription']));
        switch (buttonClicked) {
            case 'CONTINUE_BUTTON':
                dispatch(actions.setNextStep());
                break;
            case 'PREVIOUS_BUTTON':
                dispatch(actions.setPreviousStep());
                break;
            case 'REVIEW_BUTTON': {
                const reviewPhase = aPhases[aPhases.length - 1];
                dispatch(actions.setCurrentPhase(reviewPhase));
                break;
            }
            default:
                break;
        }
    };

    return (
        <div
            className="bottom-actions-wrapper"
            data-testid="bottom-actions"
            style={{
                padding: 8,
                left: 0,
                bottom: 0,
                width: '100%',
                position: 'fixed',
            }}
        >
            <Bar
                design="FloatingFooter"
                endContent={
                    <FlexBox gap={ThemingParameters.sapShell_Gap_S}>
                        {isFinalPage ? (
                            <>
                                <Link to={`/monitor/${currentFlowId}`}>
                                    <Button data-testid="monitor-button" design="Emphasized">
                                        {t('MONITOR_PROGRESS')}
                                    </Button>
                                </Link>

                                <Link
                                    to="/"
                                    onClick={() => {
                                        dispatch(actions.setIsFinalPage(false));
                                    }}
                                >
                                    <Button data-testid="home-button" design="Default">
                                        {t('HOME_BUTTON')}
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <>
                                {currentPhase.id && currentPhase?.displayOrder === aPhases.length && (
                                    <Button data-testid="launch-button" design="Emphasized" disabled={unansweredQuestions} onClick={launch}>
                                        {t('LAUNCH')}
                                    </Button>
                                )}
                                {currentPhase.id && currentPhase?.displayOrder !== aPhases.length && (
                                    <Button data-testid="continue-button" design="Emphasized" onClick={gotoNextStep}>
                                        {t('CONTINUE_BUTTON')}
                                    </Button>
                                )}
                                {currentPhase.id && currentPhase?.displayOrder !== 1 && (
                                    <Button data-testid="previous-button" design="Default" onClick={gotoPreviousStep}>
                                        {t('PREVIOUS_BUTTON')}
                                    </Button>
                                )}
                                {currentPhase.id && currentPhase?.displayOrder > 1 && currentPhase?.displayOrder <= aPhases.length - 1 && (
                                    <Button data-testid="review-button" design="Transparent" onClick={gotoReview}>
                                        {t('REVIEW_BUTTON')}
                                    </Button>
                                )}
                            </>
                        )}
                    </FlexBox>
                }
            />
            <MessageBox open={!!error} type={MessageBoxType.Error} onClose={() => reset()} data-testId="dialog_42k_error">
                <Text> {t('ALREADY_LAUNCH_STARTED')}</Text>
            </MessageBox>
            <MessageBox
                open={!!launchAribaError}
                type={MessageBoxType.Error}
                onClose={() => launchAribaReset()}
                data-testId="dialog_aribaLaunch_error"
            >
                <Text>
                    {t(
                        'ARIBA_ALREADY_LAUNCH_STARTED',
                        (launchAribaError as any)?.message.substring((launchAribaError as any)?.message.indexOf(' ') + 1),
                        formatDate((launchAribaError as any)?.message.split(' ')[0])
                    )}
                </Text>
            </MessageBox>
            <MessageBox
                open={isOpen}
                type={MessageBoxType.Warning}
                onClose={toggle}
                data-testId="continue-warning-CCDomain-change"
                actions={[
                    <Button design="Emphasized" key="okButton" data-testid="okButton" onClick={async () => onDomainChangeOkButtoClicked()}>
                        {t('OK_BUTTON')}
                    </Button>,
                    <Button design="Transparent" key="cancelButton" data-testid="cancelButton">
                        {t('CANCEL_BUTTON')}
                    </Button>,
                ]}
            >
                <Text>{t('COMMODITY_CODE_DOMAIN_CHANGED')}</Text>
                <br />
                <Text>{t('ALL_UPLOADED_DATA_LOST')}</Text>
            </MessageBox>
        </div>
    );
};

export default BottomActions;
